import { splitProps, createMemo } from "solid-js";

/**
 * @typedef {object} UIInputProps
 * @property {'xs' | 'sm' | 'md' | 'lg'} [size='md'] - Input size
 * @property {string} type - The input type, default is text
 * @property {string} [value] - Controlled value
 * @property {string} [defaultValue] - Uncontrolled default value
 * @property {(value: string) => void} [onInput] - Input change handler
 * @property {string} [className] - Additional CSS classes
 * @property {string} [label] - Label text
 * @property {string} [error] - Error message
 */

/**
 * A text input component with size variants and controlled/uncontrolled modes
 * @param {UIInputProps & import('solid-js').JSX.InputHTMLAttributes<import('solid-js').JSX.HTMLInputElement>} props component props
 * @returns {import('solid-js').JSX.Element} A UIInput
 */
export function UIInput(props) {
  const [local, others] = splitProps(props, [
    "type",
    "size",
    "className",
    "label",
    "error",
    "value",
    "onInput",
  ]);

  const inputType = createMemo(() => local.type || "text");

  const sizeClasses = {
    xs: "tw:d-input-xs",
    sm: "tw:d-input-sm",
    md: "tw:d-input-md",
    lg: "tw:d-input-lg",
  };

  const textClasses = {
    xs: "tw:text-meta",
    sm: "tw:text-meta",
    md: "tw:text-dense",
    lg: "tw:text-body",
  };

  const inputClasses = createMemo(() => {
    const sizeClass = sizeClasses[local.size || "md"];
    return [
      "tw:d-input",
      "tw:font-mono",
      "tw:focus:outline-none",
      sizeClass,
      textClasses[local.size || "md"],
      local.error ? "tw:d-input-error" : "",
      local.className || "",
    ]
      .filter(Boolean)
      .join(" ");
  });

  const handleInput = (e) => {
    if (local.onInput) {
      local.onInput(e);
    }
  };

  const InnerInput = () => {
    return (
      <input
        type={inputType()}
        className={inputClasses()}
        value={local.value}
        onInput={handleInput}
        {...others}
      />
    );
  };

  return (
    <div className="tw:flex tw:w-full tw:flex-col">
      {local.label ? (
        <label className="tw:d-floating-label tw:text-meta">
          <span>
            {local.label}{" "}
            {others.required && <span className="tw:text-error">*</span>}
          </span>
          <InnerInput />
        </label>
      ) : (
        <InnerInput />
      )}
      {local.error && (
        <label className="tw:d-label tw:text-meta">
          <span className="tw:text-error">{local.error}</span>
        </label>
      )}
    </div>
  );
}
